"use client";
import { hasConsent } from "./consent.js";
const DEFAULT_POSTHOG_HOST = "https://app.posthog.com";
const DISTINCT_ID_KEY = "dobbix_distinct_id";
let fallbackDistinctId = null;
function getPosthogConfig() {
    const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
    if (!key) {
        return null;
    }
    const host = process.env.NEXT_PUBLIC_POSTHOG_HOST ?? DEFAULT_POSTHOG_HOST;
    return { key, host: host.replace(/\/$/, "") };
}
function getStorage() {
    if (typeof window === "undefined") {
        return null;
    }
    try {
        return window.localStorage;
    }
    catch {
        return null;
    }
}
function createDistinctId() {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
        return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}
function getDistinctId() {
    if (typeof window === "undefined") {
        return "server";
    }
    const storage = getStorage();
    if (!storage) {
        fallbackDistinctId ??= createDistinctId();
        return fallbackDistinctId;
    }
    try {
        const existing = storage.getItem(DISTINCT_ID_KEY);
        if (existing) {
            return existing;
        }
        const id = createDistinctId();
        storage.setItem(DISTINCT_ID_KEY, id);
        return id;
    }
    catch {
        fallbackDistinctId ??= createDistinctId();
        return fallbackDistinctId;
    }
}
function getSafeUrl() {
    if (typeof window === "undefined") {
        return null;
    }
    return `${window.location.origin}${window.location.pathname}`;
}
function sendPosthogEvent(payload) {
    const config = getPosthogConfig();
    if (!config || typeof window === "undefined") {
        return;
    }
    const body = JSON.stringify(payload);
    const endpoint = `${config.host}/capture/`;
    if (navigator.sendBeacon) {
        navigator.sendBeacon(endpoint, body);
        return;
    }
    void fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
    });
}
function sendSupabaseEvent(event, sessionId, properties) {
    if (typeof window === "undefined")
        return;
    const body = JSON.stringify({
        event_name: event,
        session_id: sessionId,
        properties: properties ?? {},
        timestamp: new Date().toISOString(),
    });
    if (navigator.sendBeacon) {
        navigator.sendBeacon("/api/analytics/events", body);
        return;
    }
    void fetch("/api/analytics/events", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
    });
}
/**
 * Send an untyped analytics event. Respects consent gating.
 * Dual-writes to PostHog (if configured) and Supabase (via API route).
 * Prefer {@link track} for type-safe events.
 */
export function trackEvent(event, properties) {
    if (!hasConsent())
        return;
    if (typeof window === "undefined")
        return;
    const sessionId = getDistinctId();
    const safeUrl = getSafeUrl();
    const enrichedProps = {
        ...properties,
        ...(safeUrl ? { $current_url: safeUrl } : {}),
    };
    // PostHog sink
    const config = getPosthogConfig();
    if (config) {
        sendPosthogEvent({
            api_key: config.key,
            event,
            distinct_id: sessionId,
            properties: enrichedProps,
        });
    }
    // Supabase sink (via portal API route)
    sendSupabaseEvent(event, sessionId, enrichedProps);
}
/**
 * Type-safe analytics event tracking. Respects consent gating.
 *
 * @example
 * ```ts
 * track("signup_started", { method: "google" });
 * track("channel_connected", { channel: "whatsapp" });
 * ```
 */
export function track(event, properties) {
    trackEvent(event, properties);
}
/** Track a pageview. Respects consent gating. */
export function trackPageview(pathname) {
    trackEvent("$pageview", {
        $pathname: pathname,
    });
}
